import React from 'react';
import {
  Calculator, Stethoscope, MapPin, Briefcase, PiggyBank,
  ArrowLeftRight, Scale, HelpCircle, BookOpen, X, Sparkles,
  Gift, Clock, CalendarClock, Timer, Coins, PieChart, Home, Building2, Baby, Bandage, Heart, GraduationCap, HandCoins, Syringe, Umbrella, HardHat,
} from 'lucide-react';

export type TabType = 'home' | 'calculator' | 'monthly' | 'nhs' | 'nurse' | 'scottish' | 'ir35' | 'contractor' | 'umbrella' | 'pension' | 'reverse' | 'comparison' | 'taxcode' | 'selfEmployed' | 'bonus' | 'hourlyToSalary' | 'proRata' | 'overtime' | 'minimumWage' | 'dividendTax' | 'mortgage' | 'stampDuty' | 'childBenefit' | 'statutorySickPay' | 'maternityPay' | 'teacherPay' | 'taxFreeChildcare' | 'blog' | 'about' | 'write-for-us' | 'contact' | 'privacy' | 'terms';

interface NavItem {
  id: TabType;
  label: string;
  icon: React.ReactNode;
  desc?: string;
}

export const NAV_ITEMS: NavItem[] = [
  { id: 'calculator', label: 'Gross to Net', icon: <Calculator className="w-4 h-4 text-emerald-400" />, desc: 'Standard UK salary & tax breakdown' },
  { id: 'monthly', label: 'Monthly Take-Home', icon: <CalendarClock className="w-4 h-4 text-emerald-400" />, desc: 'Monthly net pay from an annual salary' },
  { id: 'nhs', label: 'NHS Salary', icon: <Stethoscope className="w-4 h-4 text-blue-400" />, desc: 'Agenda for Change pay bands & pension' },
  { id: 'nurse', label: 'Nurse Pay', icon: <Syringe className="w-4 h-4 text-rose-400" />, desc: 'Band 5-7 nurse take-home & NHS pension' },
  { id: 'scottish', label: 'Scottish Tax', icon: <MapPin className="w-4 h-4 text-indigo-400" />, desc: 'Scottish Parliament income tax rates' },
  { id: 'ir35', label: 'IR35 Contractor', icon: <Briefcase className="w-4 h-4 text-purple-400" />, desc: 'Inside vs Outside IR35 net pay' },
  { id: 'contractor', label: 'Contractor Pay', icon: <HardHat className="w-4 h-4 text-orange-400" />, desc: 'Day rate to take-home, Ltd vs umbrella' },
  { id: 'umbrella', label: 'Umbrella Pay', icon: <Umbrella className="w-4 h-4 text-sky-400" />, desc: 'Umbrella margin, employer NI & PAYE' },
  { id: 'pension', label: 'Salary Sacrifice', icon: <PiggyBank className="w-4 h-4 text-emerald-400" />, desc: 'Pension contribution tax efficiency' },
  { id: 'reverse', label: 'Net to Gross', icon: <ArrowLeftRight className="w-4 h-4 text-teal-400" />, desc: 'Calculate gross needed for target net' },
  { id: 'comparison', label: 'Compare Pay', icon: <Scale className="w-4 h-4 text-amber-400" />, desc: 'Side-by-side job offer comparison' },
  { id: 'taxcode', label: 'Tax Code', icon: <HelpCircle className="w-4 h-4 text-cyan-400" />, desc: 'Decode 1257L, BR, K, NT codes' },
  { id: 'selfEmployed', label: 'Self-Employed', icon: <Briefcase className="w-4 h-4 text-orange-400" />, desc: 'Sole trader Income Tax & Class 4 NI' },
  { id: 'bonus', label: 'Bonus Tax', icon: <Gift className="w-4 h-4 text-pink-400" />, desc: 'Take-home from a bonus payment' },
  { id: 'hourlyToSalary', label: 'Hourly to Salary', icon: <Clock className="w-4 h-4 text-sky-400" />, desc: 'Convert an hourly rate to annual salary' },
  { id: 'proRata', label: 'Pro Rata Salary', icon: <CalendarClock className="w-4 h-4 text-lime-400" />, desc: 'Part-time salary from full-time equivalent' },
  { id: 'overtime', label: 'Overtime Pay', icon: <Timer className="w-4 h-4 text-red-400" />, desc: 'Extra take-home from overtime hours' },
  { id: 'minimumWage', label: 'Minimum Wage', icon: <Coins className="w-4 h-4 text-yellow-400" />, desc: 'NLW/NMW take-home by age band' },
  { id: 'dividendTax', label: 'Dividend Tax', icon: <PieChart className="w-4 h-4 text-violet-400" />, desc: 'Tax on dividend income 2025/26' },
  { id: 'mortgage', label: 'Mortgage Repayments', icon: <Home className="w-4 h-4 text-teal-400" />, desc: 'Monthly repayment & total interest' },
  { id: 'stampDuty', label: 'Stamp Duty', icon: <Building2 className="w-4 h-4 text-indigo-400" />, desc: 'SDLT for movers, first-time buyers & BTL' },
  { id: 'childBenefit', label: 'Child Benefit', icon: <Baby className="w-4 h-4 text-fuchsia-400" />, desc: 'High Income Child Benefit Charge (HICBC)' },
  { id: 'statutorySickPay', label: 'Statutory Sick Pay', icon: <Bandage className="w-4 h-4 text-sky-400" />, desc: 'SSP after the 3 waiting days' },
  { id: 'maternityPay', label: 'Maternity Pay', icon: <Heart className="w-4 h-4 text-rose-400" />, desc: 'SMP across the full 39-week period' },
  { id: 'teacherPay', label: 'Teacher Salary', icon: <GraduationCap className="w-4 h-4 text-amber-400" />, desc: 'STPCD pay scales & TPS pension' },
  { id: 'taxFreeChildcare', label: 'Tax-Free Childcare', icon: <HandCoins className="w-4 h-4 text-teal-400" />, desc: 'Government top-up on childcare costs' },
];

interface SidebarProps {
  activeTab: TabType;
  onSelectTab: (tab: TabType) => void;
  isMobileOpen: boolean;
  onCloseMobile: () => void;
}

const Brand: React.FC<{ onClick: () => void; compact?: boolean }> = ({ onClick, compact }) => (
  <div className="flex items-center gap-3 cursor-pointer" onClick={onClick}>
    <div className="bg-gradient-to-tr from-emerald-500 to-teal-400 p-2.5 rounded-xl shadow-lg shadow-emerald-500/20 text-zinc-950 shrink-0">
      <Calculator className="w-5 h-5" />
    </div>
    <div className="min-w-0">
      <p className="text-sm font-extrabold tracking-tight text-white leading-tight">
        {compact ? 'Take Home Pay' : (
          <>
            Take Home Pay
            <br />
            Calculators
          </>
        )}
      </p>
      <span className="inline-block mt-1 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-[10px] px-2 py-0.5 rounded-full font-bold">
        2025/26
      </span>
    </div>
  </div>
);

const NavList: React.FC<{ activeTab: TabType; onSelect: (tab: TabType) => void; showDesc?: boolean }> = ({ activeTab, onSelect, showDesc }) => (
  <nav className="space-y-1">
    {NAV_ITEMS.map((item) => {
      const isActive = activeTab === item.id;
      return (
        <button
          key={item.id}
          onClick={() => onSelect(item.id)}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all border ${
            isActive
              ? 'bg-emerald-500/15 border-emerald-500/60 text-white'
              : 'bg-transparent border-transparent hover:bg-zinc-900 text-zinc-300 hover:text-white'
          }`}
        >
          <span className="shrink-0">{item.icon}</span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold truncate">{item.label}</span>
            {showDesc && item.desc && <span className="block text-[11px] text-zinc-500 truncate">{item.desc}</span>}
          </span>
        </button>
      );
    })}
  </nav>
);

export const Sidebar: React.FC<SidebarProps> = ({ activeTab, onSelectTab, isMobileOpen, onCloseMobile }) => {
  const handleSelect = (tab: TabType) => {
    onSelectTab(tab);
    onCloseMobile();
  };

  return (
    <>
      {/* Desktop — always-visible fixed sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:left-0 lg:w-64 bg-zinc-950 border-r border-zinc-800/80 z-40">
        <div className="p-5 border-b border-zinc-800/60">
          <Brand onClick={() => handleSelect('home')} />
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          <NavList activeTab={activeTab} onSelect={handleSelect} />
        </div>

        <div className="p-3 border-t border-zinc-800/60">
          <button
            onClick={() => handleSelect('blog')}
            className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-bold border transition-all ${
              activeTab === 'blog'
                ? 'bg-amber-500 text-zinc-950 border-amber-400'
                : 'bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border-amber-500/30'
            }`}
          >
            <BookOpen className="w-4 h-4" />
            <span>Blog &amp; Tax Guides</span>
          </button>
        </div>
      </aside>

      {/* Mobile — slide-down drawer, triggered by TopBar's hamburger */}
      {isMobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex flex-col bg-zinc-950">
          <div className="flex items-center justify-between p-4 border-b border-zinc-800/60">
            <Brand onClick={() => handleSelect('home')} compact />
            <button
              onClick={onCloseMobile}
              className="p-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-zinc-200 border border-zinc-800"
              aria-label="Close Navigation Menu"
            >
              <X className="w-5 h-5 text-rose-400" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <div className="flex items-center gap-1.5 text-xs font-bold text-zinc-400 uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
              All Calculators
            </div>
            <NavList activeTab={activeTab} onSelect={handleSelect} showDesc />

            <button
              onClick={() => handleSelect('blog')}
              className={`w-full flex items-center gap-2.5 px-3 py-3 rounded-xl text-sm font-bold border transition-all ${
                activeTab === 'blog'
                  ? 'bg-amber-500 text-zinc-950 border-amber-400'
                  : 'bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border-amber-500/30'
              }`}
            >
              <BookOpen className="w-4 h-4" />
              <span>Blog &amp; Tax Guides</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
};
